<script lang="ts">
  import { computed, defineComponent, toRefs } from "vue";
  import { useRouter } from "vue-router";

  import {
    editProps,
    useEditComponent,
  } from "frontend/uses/settings/use-edit-component";
  import { Schedule } from "frontend/interfaces/schedule";
  import { useSimpleModal } from "frontend/uses/simple-modal/use-simple-modal";
  import { useToasts } from "frontend/uses/use-toasts";
  import {
    duplicateSchedule,
    updateSchedule,
  } from "frontend/api/application/request-schedules";
  import { httpPayloadSchedule } from "frontend/parser/settings/parse-schedule";
  import { getIntervalStatus, IntervalStatus } from "shared/utils/date-utils";

  import BaseListItem from "frontend/components/base/BaseListItem.vue";

  export default defineComponent({
    components: { BaseListItem },
    props: {
      ...editProps<Schedule>(),
    },
    emits: ["refresh-needed", "refresh-archive"],
    setup(props, { emit }) {
      const { entity } = toRefs(props);
      const editComponent = useEditComponent(entity.value);

      const router = useRouter();

      const routeToSchedule = () => {
        router.push({
          name: "settings-edit-schedule",
          params: { id: entity.value.id },
        });
      };

      const { error: errorToast, success: successToast } = useToasts();

      const doDuplicateSchedule = async () => {
        const { confirm: confirmModal } = useSimpleModal();
        const { confirm } = await confirmModal(
          "Mit dieser Aktion duplizieren Sie den gesamten Zeitplan. Bitte beachten Sie, dass die Aktivierungen des Zeitplans nicht übernommen werden.",
          { title: "Zeitplan duplizieren?" }
        );
        if (confirm) {
          const result = await duplicateSchedule(
            editComponent.entityEditable.value.id
          );
          const { success } = result;
          if (success) {
            emit("refresh-needed");
            successToast("Der Zeitplan wurde erfolgreich dupliziert.");
          } else {
            errorToast("Der Zeitplan konnte nicht dupliziert werden.");
          }
        }
      };
      const isArchived = computed(() => !!entity.value.archived);
      const status = computed(() => {
        if (isArchived.value) return null;
        const statuses = entity.value.schedule_activations.map((a) =>
          getIntervalStatus(a.from, a.to)
        );
        return statuses.includes(IntervalStatus.Active)
          ? IntervalStatus.Active
          : statuses.includes(IntervalStatus.Planned)
            ? IntervalStatus.Planned
            : IntervalStatus.Inactive;
      });
      const statusText = computed(() => {
        if (isArchived.value) return "Archiviert";
        return status.value === IntervalStatus.Active
          ? "\u{1F7E2} Aktiv"
          : status.value === IntervalStatus.Planned
            ? "\u{1F7E1} Geplant"
            : "\u{26AA} Inaktiv";
      });

      const doUpdateArchivedState = async (
        schedule: Schedule,
        archived: boolean
      ) => {
        const result = await updateSchedule(
          httpPayloadSchedule({
            ...schedule,
            archived,
          })
        );

        if (result.success) {
          emit("refresh-archive");
          successToast("Erfolgreich gespeichert.");
        } else {
          errorToast("Der Zeitplan konnte nicht gespeichert werden.");
        }
      };

      return {
        ...editComponent,
        routeToSchedule,
        doDuplicateSchedule,
        doUpdateArchivedState,
        IntervalStatus,
        status,
        statusText,
        isArchived,
      };
    },
  });
</script>

<template>
  <BaseListItem
    v-bind:title="entityEditable.name"
    v-bind:subtitle="entityEditable.description ?? '-'"
    v-bind:text="statusText"
    v-bind:edit-action="!isArchived"
    v-bind:delete-action="!isArchived"
    v-bind:duplicate-action="!isArchived"
    v-bind:archive-action="!isArchived && status === IntervalStatus.Inactive"
    v-bind:unarchive-action="isArchived"
    v-bind:chevron="!isArchived"
    v-on:edit-item="saveRecord"
    v-on:delete-item="deleteRecord"
    v-on:duplicate-item="doDuplicateSchedule"
    v-on:select="routeToSchedule"
    v-on:archive-item="() => doUpdateArchivedState(entityEditable, true)"
    v-on:unarchive-item="() => doUpdateArchivedState(entityEditable, false)"
  />
</template>
